import { ref, computed, inject, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { CONFIG } from '../config';
import { mergeSiteBrand, applySiteBrandHead } from '../utils/siteBrandHead.js';
import { shouldSyncPublicCmsFromApi } from '../utils/publicCmsSource.js';
import { getPublicPropertyPayload } from '../utils/publicPropertyPayload.js';

/** @type {Record<string, { siteName: string, siteSlogan: string }>} */
let cachedBrandByLocale = {};
/** @type {Map<string, Promise<{ siteName: string, siteSlogan: string } | null>>} */
const inFlightByLocale = new Map();

function brandFromProperty(property) {
  if (!property || typeof property !== 'object') return null;
  const siteName = (property.siteName || '').trim();
  const siteSlogan = (property.siteSlogan || '').trim();
  if (!siteName && !siteSlogan) return null;
  return { siteName, siteSlogan };
}

async function fetchBrandForLocale(loc, { force = false } = {}) {
  const cached = cachedBrandByLocale[loc];
  if (!force && cached?.siteSlogan) return cached;

  let promise = inFlightByLocale.get(loc);
  if (promise && !force) {
    return promise;
  }

  promise = (async () => {
    try {
      const data = await getPublicPropertyPayload(loc);
      const next = data?.property
        ? {
            siteName: (data.property.siteName || '').trim(),
            siteSlogan: (data.property.siteSlogan || '').trim()
          }
        : null;
      if (next) cachedBrandByLocale[loc] = next;
      return next || cached || null;
    } catch {
      return cached || null;
    } finally {
      inFlightByLocale.delete(loc);
    }
  })();
  inFlightByLocale.set(loc, promise);
  return promise;
}

/**
 * Published site brand: canonical siteName + per-locale siteSlogan from GET /v1/property/:locale.
 */
export function useSiteBrand() {
  const initialState = inject('initialState', {});
  const publishedByLocale = inject('publishedPropertyContentByLocale', {});
  const route = useRoute();
  const remoteBrand = ref(null);
  const loading = ref(false);

  const locale = computed(() => {
    const lang = route.params?.lang;
    if (lang && CONFIG.SUPPORTED_LOCALES.includes(lang)) return lang;
    return initialState?.locale || CONFIG.DEFAULT_LOCALE;
  });

  const embeddedBrand = computed(() => {
    const stateLocale = initialState?.locale;
    if (stateLocale && stateLocale !== locale.value) return null;
    return brandFromProperty(initialState?.property);
  });

  const bundledBrand = computed(() => {
    const primary = brandFromProperty(publishedByLocale[locale.value]?.property);
    const fallback =
      locale.value !== CONFIG.DEFAULT_LOCALE
        ? brandFromProperty(publishedByLocale[CONFIG.DEFAULT_LOCALE]?.property)
        : null;
    return mergeSiteBrand(primary, fallback);
  });

  const brand = computed(() => {
    if (!shouldSyncPublicCmsFromApi()) {
      return bundledBrand.value;
    }
    const loc = locale.value;
    const primary =
      remoteBrand.value || cachedBrandByLocale[loc] || embeddedBrand.value || null;
    const fallback =
      loc !== CONFIG.DEFAULT_LOCALE ? cachedBrandByLocale[CONFIG.DEFAULT_LOCALE] || null : null;
    return mergeSiteBrand(primary, fallback);
  });

  const siteName = computed(() => brand.value.siteName);
  const siteSlogan = computed(() => brand.value.siteSlogan);

  async function fetchBrand() {
    const loc = locale.value;

    if (!shouldSyncPublicCmsFromApi()) {
      remoteBrand.value = bundledBrand.value;
      if (initialState && typeof initialState === 'object' && bundledBrand.value) {
        initialState.property = {
          ...(initialState.property || {}),
          siteName: bundledBrand.value.siteName,
          siteSlogan: bundledBrand.value.siteSlogan
        };
      }
      return;
    }

    loading.value = true;
    try {
      const tasks = [fetchBrandForLocale(loc, { force: true })];
      if (loc !== CONFIG.DEFAULT_LOCALE) {
        tasks.push(fetchBrandForLocale(CONFIG.DEFAULT_LOCALE, { force: true }));
      }
      await Promise.all(tasks);
      if (locale.value !== loc) return;

      const merged = mergeSiteBrand(
        cachedBrandByLocale[loc] || embeddedBrand.value || null,
        loc !== CONFIG.DEFAULT_LOCALE ? cachedBrandByLocale[CONFIG.DEFAULT_LOCALE] : null
      );
      remoteBrand.value = merged;

      if (initialState && typeof initialState === 'object') {
        initialState.property = {
          ...(initialState.property || {}),
          siteName: merged.siteName,
          siteSlogan: merged.siteSlogan
        };
      }
      applySiteBrandHead({
        siteName: merged.siteName,
        siteSlogan: merged.siteSlogan
      });
    } finally {
      loading.value = false;
    }
  }

  onMounted(() => {
    fetchBrand();
  });

  watch(locale, (newLoc, oldLoc) => {
    if (oldLoc === undefined || newLoc === oldLoc) return;
    remoteBrand.value = cachedBrandByLocale[newLoc] || null;
    fetchBrand();
  });

  return {
    siteName,
    siteSlogan,
    loading,
    refetch: fetchBrand
  };
}
